import $ from 'jquery';
import {isEqual} from 'lodash';
import {isEmail} from '@wordpress/url';
import {
    debounce,
    setFieldValue,
    getFieldValue,
    loadPayPalSdk,
    extractFullName,
    submitErrorMessage,
    formatPaymentMethod,
    getPayPalQueryParams,
} from '@ppcp/utils';

class FastlaneGateway {

    constructor(props) {
        this.cart = props.cart;
        this.actions = props.actions;
        this.settings = props.settings;
        this.fastlane = null;
        this.cardGateway = props.cardGateway;
        this.components = {};
        this.currentCard = null;
        this.canceledEmails = [];
        this.customerContextId = null;
    }

    initialize() {

        this.addActions();

        this.loadPayPalSDK().then(async () => {
            await this.createFastlaneInstance();
            let promises = [];
            if (this.isFastlaneIconEnabled()) {
                promises.push(this.mountFastlaneWatermark());
            }
            promises.push(this.authenticateOnPageLoad());
            await Promise.all(promises);
        }).catch(error => {

        });
    }

    isEmailDetectionEnabled() {
        return this.settings.fastlane_flow === 'email_detection';
    }

    isFastlaneIconEnabled() {
        return !!this.settings.fastlane_icon_enabled;
    }

    hasComponents() {
        return Object.keys(this.components).length > 0;
    }

    addActions() {
        this.actions.addAction('updated_checkout', this.onUpdatedCheckout.bind(this));
        $(document.body).on('input', '[name="billing_email"]', debounce(this.onEmailChange.bind(this), 250));
        $(document.body).on('click', '.wc-ppcp-fastlane-button', this.onFastlaneClick.bind(this));
        $(document.body).on('click', '.wc-ppcp-fastlane-cancel', this.onCloseModal.bind(this));
        $(document.body).on('click', '.wc-ppcp-fastlane-tokenize', this.onPaymentTokenize.bind(this));
        $(document.body).on('click', '.wc-ppcp-tokenized-card-cancel', this.cancelTokenizedPayment.bind(this));
        $(document.body).on('click', '.wc-ppcp-tokenized-card-change', this.changePaymentMethod.bind(this));
        $(document.body).on('click', '.fastlane-signup-link-container', this.openFastlaneModal.bind(this));
        $(document.body).on('blur', '.wc-ppcp-fastlane-modal-field', (e) => {
            $(e.currentTarget).removeClass('focused');
        });
        $(document.body).on('keyup', '.wc-ppcp-fastlane-modal-input', e => {
            if ($(e.currentTarget).val()) {
                $(e.currentTarget).addClass('not-empty');
            } else {
                $(e.currentTarget).removeClass('not-empty');
            }
        })
    }

    async loadPayPalSDK() {
        try {
            this.paypal = await loadPayPalSdk(getPayPalQueryParams());
            if (!this.isEmailDetectionEnabled()) {
                $('.wc-ppcp-express-checkout').show();
            }
        } catch (error) {
            console.info('Error loading paypal. ', error);
            throw error;
        }
    }

    async createFastlaneInstance() {
        if (!this.paypal.Fastlane) {
            return;
        }
        try {
            this.fastlane = await this.paypal.Fastlane();
            const {
                identity,
                profile,
                FastlanePaymentComponent,
                FastlaneCardComponent,
                FastlaneWatermarkComponent
            } = this.fastlane;

            this.identity = identity;
            this.profile = profile;
            this.components = {
                FastlanePaymentComponent,
                FastlaneCardComponent,
                FastlaneWatermarkComponent
            }
        } catch (error) {
            console.info('Error creating fastlane instance. ', error);
        }
    }

    async mountFastlaneWatermark() {
        if (this.hasComponents()) {
            try {
                if ($('#billing_email_field').length) {
                    if (!$('#billing_email_field #wc-ppcp-watermark-container').length) {
                        const component = await this.components.FastlaneWatermarkComponent({includeAdditionalInfo: true});
                        $('#billing_email_field').append('<div id="wc-ppcp-watermark-container"></div>');
                        component.render('#wc-ppcp-watermark-container');
                    }
                }
            } catch (error) {
                console.info('Error mounting watermark. Reason: ', error);
            }
        }
    }

    async authenticateOnPageLoad() {
        if (!this.fastlane || this.settings.fastlane_pageload !== '1') {
            return;
        }
        if ($('#billing_email').length) {
            const email = $('#billing_email').val();
            if (isEmail(email)) {
                // Wait for checkout to be fully initialized
                await this.delay(1000);
                await this.triggerAuthentication(email, false);
            }
        }
    }

    onUpdatedCheckout() {
        this.loadPayPalSDK().then(async () => {
            if (this.currentCard) {
                this.hideSignupLink();
                this.displayTokenizedCard(this.mapFromTokenToCard(this.currentCard));
            }
            if (this.isFastlaneIconEnabled()) {
                await this.mountFastlaneWatermark();
            }
        }).catch(error => {

        });
    }

    async onPaymentTokenize(e) {
        if (this.paymentComponent) {
            const $button = $(e.currentTarget);
            $button.prop('disabled', true);
            try {

                const response = await this.paymentComponent.getPaymentToken();
                this.currentCard = response;
                this.prepareCardGatewayForCheckout(this.currentCard);
                this.onCloseModal();
                this.displayTokenizedCard(this.mapFromTokenToCard(this.currentCard));
                let address = {};
                if (response?.paymentSource?.card?.name) {
                    const name = extractFullName(response.paymentSource.card.name);
                    address = {
                        firstName: name[0],
                        lastName: name[1],
                        ...address
                    }
                    if (response?.paymentSource?.card?.billingAddress?.phoneNumber) {
                        address.phoneNumber = response.paymentSource.card.billingAddress.phoneNumber;
                    }
                }
                if (response?.paymentSource?.card?.billingAddress) {
                    if (response.paymentSource.card.billingAddress.toPpSdkType) {
                        address = {
                            ...address,
                            ...response.paymentSource.card.billingAddress.toPpSdkType()
                        };
                    } else {
                        let a = response.paymentSource.card.billingAddress;
                        address = {
                            ...address,
                            ...{
                                addressLine1: a.streetAddress,
                                addressLine2: a.extendedAddress,
                                postalCode: a.postalCode,
                                adminArea2: a.locality,
                                adminArea1: a.region,
                                countryCode: a.countryCodeAlpha2
                            }
                        };
                        if (this.cart.needsShipping()) {
                            this.populateShippingAddress(address);
                        }
                    }
                    await this.updateBillingAddress(address);
                }

                this.actions.doAction('ppcp_fastlane_payment_tokenized', this);

                this.scrollToPlaceOrder();
                $('[name="wc-ppcp_card-payment-token"][value="new"]').trigger('click');
            } catch (error) {
                submitErrorMessage(error, $('form.checkout'), 'checkout');
            } finally {
                $button.prop('disabled', false);
            }
        }
    }

    async onEmailChange(e) {
        if (this.isEmailDetectionEnabled() && this.fastlane) {
            const email = $(e.currentTarget).val();
            if (!this.canceledEmails.includes(email) && isEmail(email)) {
                await this.triggerAuthentication(email, false);
            }
        }
    }

    async onFastlaneClick(e) {
        e.preventDefault();
        this.$button = $(e.currentTarget);
        const email = $('#billing_email').val();
        try {
            this.$button.addClass('processing').prop('disabled', true);
            this.validateEmail(email);
            await this.triggerAuthentication(email)
        } catch (error) {
            return submitErrorMessage(error, $('form.checkout'), 'checkout');
        } finally {
            if (this.$button) {
                this.$button.removeClass('processing').prop('disabled', false);
            }
        }
    }

    async triggerAuthentication(email, showSignup = true) {
        try {
            this.authenticationState = null;
            const {
                customerContextId
            } = await this.identity.lookupCustomerByEmail(email);
            if (customerContextId) {
                this.customerContextId = customerContextId;
                const {
                    profileData,
                    authenticationState
                } = await this.identity.triggerAuthenticationFlow(customerContextId);

                this.authenticationState = authenticationState;
                if (authenticationState === 'canceled') {
                    this.canceledEmails.push(email);
                    this.hideSignupLink();
                } else if (authenticationState === 'succeeded') {
                    let billingAddress;
                    let {
                        name,
                        card,
                        shippingAddress
                    } = profileData;

                    this.currentCard = card;

                    /**
                     * 1. If shipping is required, show the shipping selector.
                     * 2. Let them select their payment method.
                     */

                    if (this.cart.needsShipping()) {
                        const {
                            selectedAddress
                        } = await this.profile.showShippingAddressSelector();

                        if (!selectedAddress) {
                            this.canceledEmails.push(email);
                            this.hideSignupLink();
                            return;
                        }
                        shippingAddress = {
                            ...selectedAddress.name,
                            ...selectedAddress.address,
                            ...(selectedAddress?.phoneNumber?.nationalNumber && {
                                phoneNumber: selectedAddress.phoneNumber.nationalNumber
                            })
                        };
                    }


                    if (card?.paymentSource?.card?.billingAddress) {
                        billingAddress = card.paymentSource.card.billingAddress;
                        this.populateBillingAddress({
                            ...name,
                            ...billingAddress,
                            phoneNumber: shippingAddress?.phoneNumber || null
                        });
                    }
                    if (shippingAddress && this.cart.needsShipping()) {
                        await this.updateShippingAddress(shippingAddress, billingAddress);
                    }
                    this.hideSignupLink();
                    this.displayTokenizedCard(this.mapFromTokenToCard(card));
                    this.prepareCardGatewayForCheckout(card);
                    this.actions.doAction('ppcp_fastlane_payment_tokenized', this);
                    this.scrollToPlaceOrder();
                }
            } else if (showSignup) {
                await this.openFastlaneModal();
            } else {
                this.showSignupLink();
            }
        } catch (error) {
            submitErrorMessage(error, $('form.checkout'), 'checkout');
        }
    }

    async openFastlaneModal(component) {
        this.customerContextId = null;
        this.paymentComponent = await this.components.FastlanePaymentComponent({
            ...(this.cart.needsShipping() && {
                shippingAddress: this.getFastlaneShippingAddress()
            })
        });
        this.$modal = $(this.settings.html.modal);
        $(document.body).prepend(this.$modal);
        $(document.body).addClass('fastlane-modal-open');
        this.$modal.find('.wc-ppcp-fastlane-modal-field').on('mousedown', (e) => {
            $(e.currentTarget).addClass('focused');
        });

        await this.paymentComponent.render('.wc-ppcp-fastlane-modal-body');

        setTimeout(() => {
            this.$modal.addClass('active');
        }, 10);
    }

    onCloseModal() {
        if (this.$modal) {
            this.$modal.removeClass('active');
            setTimeout(() => {
                this.$modal.remove();
                this.$modal = null;
                $(document.body).removeClass('fastlane-modal-open');
            }, 400);
        }
    }

    getFastlaneShippingAddress() {
        const shippingAddress = {
            firstName: getFieldValue('shipping_first_name'),
            lastName: getFieldValue('shipping_last_name'),
            streetAddress: getFieldValue('shipping_address_1'),
            extendedAddress: getFieldValue('shipping_address_2'),
            locality: getFieldValue('shipping_city'),
            region: getFieldValue('shipping_state'),
            postalCode: getFieldValue('shipping_postcode'),
            countryCodeAlpha2: getFieldValue('shipping_country'),
            phoneNumber: getFieldValue('billing_phone') || '',
        }
        return shippingAddress;
    }

    mapFromTokenToCard(data) {
        const card = data.paymentSource.card;
        return {
            brand: card.brand,
            last4: card.lastDigits,
            expiry: card.expiry
        }
    }

    displayTokenizedCard(card) {
        let text = formatPaymentMethod(this.settings.payment_format, card);
        if (this.$card) {
            this.$card.remove();
            this.$card = null;
        }
        this.$card = $(this.settings.html.tokenized_card);
        if (this.customerContextId) {
            this.$card.addClass('has-customer-context');
        }
        this.$card.find('.wc-ppcp-tokenized-card-format').text(text);
        if (this.settings.icons[card.brand.toLowerCase()]) {
            const src = this.settings.icons[card.brand.toLowerCase()];
            this.$card.find('.wc-ppcp-tokenized-card-icon-container img').attr('src', src);
        }

        $('.wc-ppcp_card-payment-method__container').hide();
        $('.wc-ppcp_card-payment-method__container').after(this.$card);
    }

    cancelTokenizedPayment() {
        this.currentCard = null;
        this.authenticationState = null;
        if (!this.customerContextId) {
            this.showSignupLink();
        }
        if (this.$card) {
            this.$card.remove();
            this.$card = null;
            $('.wc-ppcp_card-payment-method__container').show();
            this.cardGateway.setPaymentToken(null);
            this.cardGateway.setIsFastlane(false);
        }
    }

    async changePaymentMethod() {
        try {
            const {
                selectedCard,
                selectionChanged
            } = await this.profile.showCardSelector();
            if (selectedCard) {
                this.cancelTokenizedPayment();
                this.currentCard = selectedCard;
                this.displayTokenizedCard(this.mapFromTokenToCard(selectedCard));
                this.prepareCardGatewayForCheckout(selectedCard);
                if (selectedCard?.paymentSource?.card?.billingAddress) {
                    let address = selectedCard.paymentSource.card.billingAddress;
                    const name = extractFullName(selectedCard.paymentSource.card.name);
                    address = {
                        firstName: name[0],
                        lastName: name[1],
                        ...address
                    }
                    await this.updateBillingAddress(address);
                }
            }
        } catch (error) {
            submitErrorMessage(error, $('form.checkout'), 'checkout');
        }
    }

    populateBillingAddress(address) {
        const existing = {
            firstName: $('#billing_first_name').val(),
            lastName: $('#billing_last_name').val(),
            phoneNumber: $('#billing_phone').val()
        }
        setFieldValue('billing_first_name', address?.firstName || existing.firstName);
        setFieldValue('billing_last_name', address?.lastName || existing.lastName);
        setFieldValue('billing_address_1', address?.addressLine1 || '');
        setFieldValue('billing_address_2', address?.addressLine2 || '');
        setFieldValue('billing_postcode', address?.postalCode || '');
        setFieldValue('billing_city', address?.adminArea2 || '');
        setFieldValue('billing_state', address?.adminArea1 || '');
        setFieldValue('billing_country', address?.countryCode || '');
        setFieldValue('billing_phone', address?.phoneNumber || existing.phoneNumber);
    }

    populateShippingAddress(address) {
        setFieldValue('shipping_first_name', address?.firstName || '');
        setFieldValue('shipping_last_name', address?.lastName || '');
        setFieldValue('shipping_address_1', address?.addressLine1 || '');
        setFieldValue('shipping_address_2', address?.addressLine2 || '');
        setFieldValue('shipping_postcode', address?.postalCode || '');
        setFieldValue('shipping_city', address?.adminArea2 || '');
        setFieldValue('shipping_state', address?.adminArea1 || '');
        setFieldValue('shipping_country', address?.countryCode || '');
        setFieldValue('shipping_phone', address?.phoneNumber || '');
    }

    updateBillingAddress(address) {
        return new Promise((resolve) => {
            this.populateBillingAddress(address);
            $(document.body).one('updated_checkout', () => {
                resolve(true);
            });
            $(document.body).trigger('update_checkout');
        });
    }

    updateShippingAddress(shippingAddress, billingAddress = null) {
        return new Promise(resolve => {
            this.populateShippingAddress(shippingAddress);
            if (billingAddress && !isEqual(billingAddress, shippingAddress)) {
                if (!$('[name="ship_to_different_address"]').is(':checked')) {
                    $('[name="ship_to_different_address"]').trigger('click');
                }
            }
            $(document.body).one('updated_checkout', () => {
                resolve(true);
            });
            $(document.body).trigger('update_checkout');
        });
    }

    prepareCardGatewayForCheckout(token) {
        this.cardGateway.setPaymentToken(token.id);
        this.cardGateway.setIsFastlane(true);
        setFieldValue('ppcp_card_payment_token', token.id, null);
        $('input[name="payment_method"][value="ppcp_card"]').trigger('click');
    }

    validateEmail(email) {
        if (!email) {
            throw new Error(this.settings.i18n.email_empty);
        }
        if (!isEmail(email)) {
            throw new Error(this.settings.i18n.email_invalid);
        }
    }

    scrollToPlaceOrder() {
        const $el = $('#place_order');
        if ($el.length) {
            $('html, body').animate(
                {
                    scrollTop: $el.offset().top - 200,
                },
                1000
            );
        }
    }

    hideSignupLink() {
        $('.fastlane-signup-link-container').hide();
    }

    showSignupLink() {
        $('.fastlane-signup-link-container').show();
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

}

export default FastlaneGateway;
export {
    FastlaneGateway
}